import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckCheck, ChevronLeft, ChevronRight, Lock } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { toastHelpers } from '@/lib/toast-helpers'
import { addDaysISO, dayLabel, leaveTypeLabel, STATUS_LABEL, todayISO } from '@/lib/hr'
import { cn } from '@/lib/utils'
import type { HRAttendanceStatus, HRLeaveType, HRMarkEntry, HRRegisterDay, HRRegisterRow } from '@/types/hr'
import { Banner, Empty, NativeSelect, StatusChip, errMsg } from './shared'
import { hrRole } from './HRPage'

// The daily register (F-HR-N2). "Mark everyone present" first, then tap the
// exceptions. Weekly offs and holidays fill themselves in. Changing an earlier
// day asks once for a reason, which goes on the audit trail.

const CHOICES: { id: HRAttendanceStatus; label: string; short: string }[] = [
  { id: 'present', label: 'Present', short: 'P' },
  { id: 'late', label: 'Late', short: 'Late' },
  { id: 'half_day', label: 'Half day', short: 'Half' },
  { id: 'absent', label: 'Absent', short: 'Absent' },
  { id: 'leave', label: 'Leave', short: 'Leave' },
  { id: 'weekly_off', label: 'Off', short: 'Off' },
]

export function RegisterTab({ date: dateProp, onDate }: { date?: string; onDate: (d: string) => void }) {
  const qc = useQueryClient()
  const { canMark } = hrRole()
  const today = todayISO()
  const date = dateProp && /^\d{4}-\d{2}-\d{2}$/.test(dateProp) ? dateProp : today
  const [onlyUnmarked, setOnlyUnmarked] = useState(false)
  const [reasons, setReasons] = useState<Record<string, string>>({})
  const [pending, setPending] = useState<HRMarkEntry | null>(null)
  const [reasonDraft, setReasonDraft] = useState('')

  const dayQ = useQuery({ queryKey: ['hr', 'day', date], queryFn: () => apiClient.hrGetDay(date) })
  const typesQ = useQuery({ queryKey: ['hr', 'leave-types'], queryFn: () => apiClient.hrListLeaveTypes() })
  const day = dayQ.data?.data as HRRegisterDay | undefined
  const types = (typesQ.data?.data as HRLeaveType[] | undefined) ?? []
  const typeCode = useMemo(() => Object.fromEntries(types.map((t) => [t.id, t.code])), [types])

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['hr', 'day', date] })
    qc.invalidateQueries({ queryKey: ['hr', 'overview'] })
    qc.invalidateQueries({ queryKey: ['hr', 'month'] })
  }
  const mark = useMutation({
    mutationFn: (v: { entry: HRMarkEntry; reason?: string }) => apiClient.hrMarkDay({ date, reason: v.reason, entries: [v.entry] }),
    onSuccess: refresh,
    onError: (err, v) => {
      if ((err as { code?: string })?.code === 'hr_edit_reason_required') {
        setPending(v.entry)
        return
      }
      toastHelpers.error('Couldn’t save', errMsg(err))
      refresh()
    },
  })
  const markAll = useMutation({
    mutationFn: () => apiClient.hrMarkAllPresent(date),
    onSuccess: (res) => {
      const n = res.data?.changed ?? 0
      toastHelpers.success(n ? `Marked ${n} present` : 'Everyone is already marked')
      refresh()
    },
    onError: (err) => toastHelpers.error('Couldn’t mark everyone', errMsg(err)),
  })

  const isPast = date < today
  const save = (row: HRRegisterRow, entry: Omit<HRMarkEntry, 'employee_id'>) => {
    const e: HRMarkEntry = { employee_id: row.employee_id, ...entry }
    // An earlier day that's already marked needs a reason; ask once per date.
    if (isPast && row.marked && !reasons[date]) {
      setPending(e)
      setReasonDraft('')
      return
    }
    mark.mutate({ entry: e, reason: reasons[date] })
  }

  const rows = day?.rows ?? []
  const counts = rows.reduce(
    (acc, r) => {
      const k = !r.marked && (r.status === 'weekly_off' || r.status === 'holiday') ? 'off' : r.marked ? r.status! : 'unmarked'
      acc[k] = (acc[k] ?? 0) + 1
      return acc
    },
    {} as Record<string, number>,
  )
  const shown = onlyUnmarked ? rows.filter((r) => !r.marked && r.status !== 'weekly_off' && r.status !== 'holiday') : rows
  const groups = shown.reduce(
    (acc, r) => {
      ;(acc[r.department] ??= []).push(r)
      return acc
    },
    {} as Record<string, HRRegisterRow[]>,
  )
  const editable = canMark && !!day && !day.locked

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => onDate(addDaysISO(date, -1))} aria-label="Previous day">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Input type="date" value={date} onChange={(e) => e.target.value && onDate(e.target.value)} className="h-9 w-40" />
          <Button variant="outline" size="sm" onClick={() => onDate(addDaysISO(date, 1))} aria-label="Next day">
            <ChevronRight className="h-4 w-4" />
          </Button>
          {date !== today && (
            <Button variant="ghost" size="sm" onClick={() => onDate(today)}>
              Today
            </Button>
          )}
          <span className="ml-1 text-sm font-medium">{dayLabel(date, true)}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <input type="checkbox" checked={onlyUnmarked} onChange={(e) => setOnlyUnmarked(e.target.checked)} />
            Only not marked
          </label>
          {editable && !day?.is_future && (
            <Button size="sm" onClick={() => markAll.mutate()} disabled={markAll.isPending || (counts.unmarked ?? 0) === 0}>
              <CheckCheck className="mr-1.5 h-4 w-4" />
              Mark everyone present
            </Button>
          )}
        </div>
      </div>

      {day?.holiday && <Banner>Holiday: {day.holiday}. Everyone counts as off unless you mark them.</Banner>}
      {day?.locked && (
        <Banner tone="warn">
          <Lock className="mr-1 inline h-3.5 w-3.5" />
          Payroll for {day.locked_month} is submitted or approved, so this day is locked. Send the payroll back to change it.
        </Banner>
      )}
      {day?.is_future && <Banner>This is a future date: you can only plan leave and days off.</Banner>}
      {isPast && reasons[date] && (
        <p className="text-xs text-muted-foreground">
          Changes to this day are saved with the reason: “{reasons[date]}”.{' '}
          <button className="underline" onClick={() => setReasons((r) => ({ ...r, [date]: '' }))}>
            Change reason
          </button>
        </p>
      )}

      <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
        {(['present', 'late', 'half_day', 'absent', 'leave'] as HRAttendanceStatus[]).map((s) =>
          counts[s] ? (
            <span key={s} className="rounded-md bg-muted/60 px-2 py-1">
              {STATUS_LABEL[s]} {counts[s]}
            </span>
          ) : null,
        )}
        {counts.off ? <span className="rounded-md bg-muted/60 px-2 py-1">Off {counts.off}</span> : null}
        {counts.unmarked ? (
          <span className="rounded-md bg-amber-50 px-2 py-1 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
            Not marked {counts.unmarked}
          </span>
        ) : null}
      </div>

      {dayQ.isLoading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : rows.length === 0 ? (
        <Empty>Nobody is on Staff &amp; Payroll for this date yet. Add people in the People tab.</Empty>
      ) : (
        <div className="space-y-5">
          {Object.entries(groups).map(([dept, list]) => (
            <div key={dept} className="overflow-hidden rounded-lg border">
              <div className="border-b bg-muted/40 px-4 py-2 text-sm font-medium">
                {dept} <span className="text-muted-foreground">· {list.length}</span>
              </div>
              <div className="divide-y">
                {list.map((r) => (
                  <RegisterRow
                    key={r.employee_id}
                    row={r}
                    types={types}
                    typeCode={typeCode}
                    editable={editable && r.can_edit}
                    future={!!day?.is_future}
                    busy={mark.isPending && mark.variables?.entry.employee_id === r.employee_id}
                    onSave={(entry) => save(r, entry)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Why are you changing {dayLabel(date)}?</DialogTitle>
            <DialogDescription>The day was already marked. The reason is kept with the change for the owner to see.</DialogDescription>
          </DialogHeader>
          <Textarea value={reasonDraft} onChange={(e) => setReasonDraft(e.target.value)} placeholder="e.g. Ali came in after the register was done" rows={3} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setPending(null)}>
              Cancel
            </Button>
            <Button
              disabled={!reasonDraft.trim()}
              onClick={() => {
                const reason = reasonDraft.trim()
                setReasons((r) => ({ ...r, [date]: reason }))
                if (pending) mark.mutate({ entry: pending, reason })
                setPending(null)
              }}
            >
              Save change
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function RegisterRow({
  row,
  types,
  typeCode,
  editable,
  future,
  busy,
  onSave,
}: {
  row: HRRegisterRow
  types: HRLeaveType[]
  typeCode: Record<string, string>
  editable: boolean
  future: boolean
  busy: boolean
  onSave: (e: Omit<HRMarkEntry, 'employee_id'>) => void
}) {
  const [late, setLate] = useState<string>(row.late_minutes != null ? String(row.late_minutes) : '')
  const [pickLeave, setPickLeave] = useState(false)
  const status = row.status
  const implicit = !row.marked && !!status

  const choose = (s: HRAttendanceStatus) => {
    if (s === 'leave') {
      setPickLeave(true)
      return
    }
    setPickLeave(false)
    if (s === status && row.marked) return
    onSave({ status: s, late_minutes: s === 'late' && late ? Number(late) : null })
  }

  return (
    <div className={cn('flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between', busy && 'opacity-60')}>
      <div className="min-w-0">
        <div className="truncate text-sm font-medium">{row.full_name}</div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {row.designation && <span>{row.designation}</span>}
          {!editable && <StatusChip status={status} leaveCode={row.leave_type_id ? typeCode[row.leave_type_id] : undefined} implicit={implicit} />}
          {!row.can_edit && !row.marked && <span>· marked by someone else</span>}
          {row.marked_by && <span title={row.marked_at ?? undefined}>· by {row.marked_by}</span>}
          {row.note && <span>· {row.note}</span>}
        </div>
      </div>
      {editable && (
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap gap-1 rounded-lg bg-muted/60 p-1">
            {CHOICES.filter((c) => !future || c.id === 'leave' || c.id === 'weekly_off').map((c) => {
              const active = (status === c.id && (row.marked || c.id === 'weekly_off')) || (c.id === 'leave' && pickLeave)
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => choose(c.id)}
                  className={cn(
                    'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                    active ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
                    active && c.id === 'absent' && 'text-red-700 dark:text-red-300',
                    implicit && status === c.id && 'ring-1 ring-border',
                  )}
                >
                  {c.id === 'leave' && status === 'leave' && row.leave_type_id ? `${typeCode[row.leave_type_id] ?? ''} leave` : c.short}
                </button>
              )
            })}
          </div>
          {status === 'late' && row.marked && (
            <Input
              type="number"
              min={0}
              value={late}
              placeholder="min"
              onChange={(e) => setLate(e.target.value)}
              onBlur={() => {
                const v = late === '' ? null : Number(late)
                if (v !== row.late_minutes) onSave({ status: 'late', late_minutes: v })
              }}
              className="h-8 w-20"
              aria-label="Minutes late"
            />
          )}
          {(pickLeave || (status === 'leave' && row.marked)) && (
            <NativeSelect
              className="h-8"
              value={status === 'leave' && !pickLeave ? (row.leave_type_id ?? '') : ''}
              onChange={(e) => {
                if (!e.target.value) return
                setPickLeave(false)
                onSave({ status: 'leave', leave_type_id: e.target.value })
              }}
              aria-label="Leave type"
            >
              <option value="">Which leave?</option>
              {types.map((t) => (
                <option key={t.id} value={t.id}>
                  {leaveTypeLabel(t)}
                </option>
              ))}
            </NativeSelect>
          )}
          {status === 'half_day' && row.marked && (
            <NativeSelect
              className="h-8"
              value={row.leave_type_id ?? ''}
              onChange={(e) => onSave({ status: 'half_day', leave_type_id: e.target.value || null })}
              aria-label="Other half"
            >
              <option value="">Other half: unpaid</option>
              {types.map((t) => (
                <option key={t.id} value={t.id}>
                  Other half: {t.name} leave
                </option>
              ))}
            </NativeSelect>
          )}
          {row.marked && (
            <button
              type="button"
              className="text-xs text-muted-foreground underline-offset-2 hover:underline"
              onClick={() => onSave({ status: '' })}
              title="Clear this mark"
            >
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  )
}
